import React, { useCallback } from "react";

import {
  Cell,
  Pie,
  PieChart as RechartsPieChart,
  ResponsiveContainer,
} from "recharts";

import { AssetClassCategory } from "@fartherfinance/frontend/api/PerformanceGroups/requests/getHoldings";
import { HookResult } from "@fartherfinance/frontend/api/Types";
import { useTheme } from "@fartherfinance/frontend/theme/ThemeProvider";

import useAssetCategoryColors from "@src/multiCustodian/components/Client/Accounts/Holdings/utils/useAssetCategoryColors";
import useAssetCategoryColorsV2 from "@src/multiCustodian/components/Client/Accounts/Holdings/utils/useAssetCategoryColorsV2";
import NoDataSet from "@src/multiCustodian/components/Portfolios/NoDataSet";
import ColorDotWithLabel from "@src/sharedComponents/ColorDotWithLabel/ColorDotWithLabel";
import SimpleTwoColumnFlexBetweenTableRow from "@src/sharedComponents/SimpleTwoColumnFlexBetweenTableRow/Row";
import Skeleton from "@src/sharedComponents/Skeleton/Skeleton";
import Text from "@src/sharedComponents/Typography/Text";

import styles from "./AssetAllocationPieChart.module.css";

const INNER_RADIUS_PERCENT = 48;
const OUTER_RADIUS_PERCENT = 66;

const PLACEHOLDER_DATA = [{ label: null, value: 1 }]; //value must be above 0 for the placeholder circle to appear

export type AssetClassification = {
  label: AssetClassCategory;
  value: number;
};

interface AssetAllocationPieChartProps {
  data: HookResult<AssetClassification[]>;
  useColorMappingsV1?: boolean;
}

const AssetAllocationPieChart: React.FC<AssetAllocationPieChartProps> = ({
  data,
  useColorMappingsV1 = false,
}) => {
  const assetClassCategoriesToColors = useAssetCategoryColors();
  const getColorFromLabelV2 = useAssetCategoryColorsV2();

  const getColor = useCallback(
    (label: AssetClassCategory) => {
      if (useColorMappingsV1) {
        return assetClassCategoriesToColors[label];
      }

      return getColorFromLabelV2(label);
    },
    [assetClassCategoriesToColors, getColorFromLabelV2, useColorMappingsV1]
  );

  if (data.isLoading) {
    return <Loading />;
  }

  if (data.hasError) {
    return (
      <NoDataSet text={"Error loading asset allocation"} iconColor={"error"} />
    );
  }

  if (!data.data || data.data.length === 0) {
    return <NoDataSet text={"Data set empty"} iconColor={"info"} />;
  }

  return (
    <div className={styles.container}>
      <Text className={styles.heading} variant={"heading1"} color={"text"}>
        Allocation by Asset Class
      </Text>

      <div className={styles.flex}>
        <div className={styles.chartDiv}>
          <ResponsiveContainer width="100%" height="100%">
            <RechartsPieChart>
              <Pie
                width={216}
                data={data.data}
                nameKey={"label"}
                dataKey="value"
                innerRadius={`${INNER_RADIUS_PERCENT}%`}
                outerRadius={`${OUTER_RADIUS_PERCENT}%`}
                stroke={"none"}
                labelLine={false}
                isAnimationActive={true}
              >
                {data.data.map((entry) => (
                  <Cell
                    strokeWidth={3}
                    key={entry.label}
                    fill={getColor(entry.label)}
                  />
                ))}
              </Pie>
            </RechartsPieChart>
          </ResponsiveContainer>
        </div>

        <div className={styles.tableDiv}>
          <div className={styles.flexBetween}>
            <Text variant={"body"} color={"textSubtle"}>
              Asset Class
            </Text>

            <Text variant={"body"} color={"textSubtle"}>
              Weight
            </Text>
          </div>

          {data.data.map((cat, i) => {
            return (
              <SimpleTwoColumnFlexBetweenTableRow
                key={cat.label}
                customStartText={
                  <ColorDotWithLabel
                    label={cat.label}
                    color={getColor(cat.label)}
                  />
                }
                textEnd={`${cat.value.toFixed(2)}%`}
                hideBottomBorder={i === data.data.length - 1}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AssetAllocationPieChart;

const PiePlaceholder = () => {
  const {
    color: { $iconSubtle },
  } = useTheme();

  return (
    <div className={styles.chartDiv}>
      <ResponsiveContainer width="100%" height="100%">
        <RechartsPieChart>
          <Pie
            width={216}
            data={PLACEHOLDER_DATA}
            nameKey={"label"}
            dataKey="value"
            innerRadius={`${INNER_RADIUS_PERCENT}%`}
            outerRadius={`${OUTER_RADIUS_PERCENT}%`}
            stroke={"none"}
            labelLine={false}
            isAnimationActive={false}
          >
            <Cell strokeWidth={3} fill={$iconSubtle} />
          </Pie>
        </RechartsPieChart>
      </ResponsiveContainer>
    </div>
  );
};

const Loading = () => {
  return (
    <div className={styles.container}>
      <Text className={styles.heading} variant={"heading1"} color={"text"}>
        Allocation by Asset Class
      </Text>

      <div className={styles.flex}>
        <PiePlaceholder />

        <div className={styles.tableDiv}>
          <div className={styles.flexBetween}>
            <Text variant={"body"} color={"textSubtle"}>
              Asset Class
            </Text>

            <Text variant={"body"} color={"textSubtle"}>
              Weight
            </Text>
          </div>

          {new Array(3).fill(null).map((_, i) => {
            return (
              <SimpleTwoColumnFlexBetweenTableRow
                key={i}
                customStartText={<Skeleton width={80} />}
                customEndText={<Skeleton width={20} />}
                hideBottomBorder={i === 3}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};
